import type { CartItem } from '@/hooks/useCart';
import { products } from '@/mocks/products';

interface CartItemRowProps {
  item: CartItem;
  onUpdateQty: (productId: string, qty: number) => void;
  onRemove: (productId: string) => void;
}

function formatPrice(amount: number): string {
  return `KSh ${amount.toLocaleString('en-KE')}`;
}

export default function CartItemRow({ item, onUpdateQty, onRemove }: CartItemRowProps) {
  const product = products.find((p) => p.id === item.productId);
  if (!product) return null;

  const price = product.salePrice ?? product.price;
  const total = price * item.quantity;
  const discount = product.salePrice
    ? Math.round(((product.price - product.salePrice) / product.price) * 100)
    : null;

  return (
    <div className="flex gap-3 md:gap-4 p-3 md:p-4 rounded-xl bg-white dark:bg-background-100 border border-background-200/60">
      {/* Image */}
      <a
        href={`/product/${product.slug}`}
        className="w-20 h-20 md:w-24 md:h-24 rounded-lg bg-background-100 overflow-hidden flex-shrink-0"
      >
        <img
          src={product.images[0]}
          alt={product.name}
          className="w-full h-full object-cover object-top"
        />
      </a>

      {/* Details */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <a
              href={`/product/${product.slug}`}
              className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors line-clamp-2"
            >
              {product.name}
            </a>
            <p className="text-xs text-foreground-500 mt-0.5">{product.storeName}</p>
          </div>
          <button
            type="button"
            onClick={() => onRemove(item.productId)}
            className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors"
            aria-label="Remove item"
          >
            <i className="ri-delete-bin-line text-sm text-foreground-500 hover:text-primary-600" />
          </button>
        </div>

        <div className="flex items-end justify-between mt-auto pt-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-primary-600">{formatPrice(price)}</span>
            {product.salePrice && (
              <span className="text-xs text-foreground-500 line-through">{formatPrice(product.price)}</span>
            )}
            {discount && discount > 0 && (
              <span className="text-xs text-accent-700 font-medium">-{discount}%</span>
            )}
          </div>

          <div className="flex items-center rounded-lg border border-background-200/70 overflow-hidden">
            <button
              type="button"
              onClick={() => onUpdateQty(item.productId, item.quantity - 1)}
              className="w-8 h-8 flex items-center justify-center hover:bg-background-100 transition-colors"
              aria-label="Decrease quantity"
            >
              <i className="ri-subtract-line text-foreground-700" />
            </button>
            <span className="w-10 h-8 flex items-center justify-center text-sm font-semibold text-foreground-900">
              {item.quantity}
            </span>
            <button
              type="button"
              onClick={() => onUpdateQty(item.productId, item.quantity + 1)}
              className="w-8 h-8 flex items-center justify-center hover:bg-background-100 transition-colors"
              aria-label="Increase quantity"
            >
              <i className="ri-add-line text-foreground-700" />
            </button>
          </div>
        </div>

        <p className="text-xs font-semibold text-foreground-800 mt-1 text-right md:text-left">
          Total: {formatPrice(total)}
        </p>
      </div>
    </div>
  );
}